import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchIbamaConamaBoard } from '@/services/ibama';

/**
 * The CONAMA CER reunion archive: IBAMA's collegiate third instance over the
 * historical autos. Each row is a reunion of record; the `with_relator`
 * column counts items where the transcript's speaker marks name the relator
 * person. This archive is its own track - it is never pooled with the
 * office-attribution figures on the judges bench.
 */
export function IbamaConamaBoardPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ibama', 'conama', 'board'],
    queryFn: fetchIbamaConamaBoard,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:conama.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:conama.subtitle')}</p>
        <p className="text-xs">
          <Link
            to="/ibama/judges/methodology"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('ibama:judges.methodologyLink')}
          </Link>
        </p>
      </header>

      <ExplainerCard i18nKey="ibama:conama.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <p className="text-xs text-muted-foreground" data-archive-summary>
        {t('ibama:conama.summary', {
          items: number.format(data.items),
          judged: number.format(data.judged),
          linked: number.format(data.linked),
          people: number.format(data.people),
        })}
      </p>

      <section className="rounded-lg border bg-card p-4" data-sessions>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.session')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.dates')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('ibama:conama.items')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('ibama:conama.judged')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('ibama:conama.withRelator')}</th>
                <th className="px-3 py-2 font-medium">{t('ibama:conama.source')}</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.map((s) => (
                <tr key={s.reunion_number} className="border-t" data-session={s.reunion_number}>
                  <td className="px-3 py-2 text-foreground">
                    <Link
                      to={`/ibama/conama/reunion/${s.reunion_number}`}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {s.reunion_number}a
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {s.held_from ?? '—'}
                    {s.held_to && s.held_to !== s.held_from ? ` – ${s.held_to}` : ''}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">
                    {number.format(s.items)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {number.format(s.judged)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {number.format(s.with_relator)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <a
                      href={s.page_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {t('ibama:conama.sourceLink')}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Link to="/ibama/judges" className="text-xs text-primary underline-offset-2 hover:underline">
            {t('ibama:conama.relatorsLink')}
          </Link>
        </div>
      </section>

      <FieldGuide i18nKey="ibama:conama.guide" />
    </div>
  );
}
